import { useEffect, useState } from "react";
import clsx from "clsx";

// Components
import ButtonIconText, { TButtonIconTextProps } from "@/Layouts/components/Buttons/ButtonIconText";
import Button from "@/Layouts/components/Buttons/Button";

// MUI
import { CircularProgress, Dialog, DialogContent, DialogProps, DialogTitle, Divider, IconButton, Stack, Typography } from "@mui/material";

// Icons
import CloseIcon from "@mui/icons-material/Close";

type TSelectionContentProps = {
	firstContent?: React.ReactNode;
	secondContent?: React.ReactNode;
	selected?: string | null;
};

function SelectionContent(props: Readonly<TSelectionContentProps>) {
	if (props.selected === "first") {
		return <>{props.firstContent}</>;
	}
	return <>{props.secondContent}</>;
}

type TSelectionDialogProps = {
	open: boolean;
	onClose?: (value: string | null) => void;
	onChangeSelection?: (value: string | null) => void;
	onAddAction?: (value: string | null) => void;
	selected?: string;
	values?: { first: string; second: string };
	icons?: { first: React.ReactNode; firstHover: React.ReactNode; second: React.ReactNode; secondHover: React.ReactNode };
	iconsProps?: { first?: Partial<TButtonIconTextProps>; second?: Partial<TButtonIconTextProps> };
	firstContent?: React.ReactNode;
	secondContent?: React.ReactNode;
	buttonText?: string;
	disableClose?: boolean;
	disabled?: boolean;
	disabledFirst?: boolean;
	disabledSecond?: boolean;
	loading?: boolean;
	maxWidth?: DialogProps["maxWidth"];
	fullWidth?: boolean;
	sx?: object;
};

export default function SelectionDialog(props: Readonly<TSelectionDialogProps>) {
	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState<string | null>(null);
	const [values, setValues] = useState<Record<string, string>>({
		first: "Check",
		second: "Rollover",
	});

	const handleClose = () => {
		setOpen(false);
		if (props.onClose) {
			// Return last selected value
			props.onClose(selected);
		}
	};

	const handleSelection = (value?: string | null) => {
		setSelected(value ?? null);
		if (props.onChangeSelection) {
			props.onChangeSelection(value ?? null);
		}
	};

	const handleAdd = () => {
		if (props.onAddAction) {
			props.onAddAction(selected);
		}
	};

	const getValue = (selectedValue: unknown) => {
		if (!selectedValue) {
			return null;
		}
		return Object.keys(values).find((key) => values[key] === selectedValue);
	};

	useEffect(() => {
		setOpen(props?.open);

		// Values must be defined as an object like {first: "First value", second: "Second value"}
		if (props.values) {
			setValues(props.values);
		}

		if (props.selected) {
			setSelected(props.selected);
		}

		return () => {
			setOpen(false);
			setSelected(null);
			setValues({
				first: "Check",
				second: "Rollover",
			});
		};
	}, [props]);

	return (
		<Dialog maxWidth={props.maxWidth ?? "xs"} fullWidth={props.fullWidth} open={open} sx={props.sx ?? null} onClose={handleClose}>
			<DialogTitle id="selection-dialog-dialog-title" className="min-h-[30px]">
				{props.onClose ? (
					<IconButton
						disabled={props.disableClose}
						aria-label="close"
						onClick={handleClose}
						sx={{
							position: "absolute",
							right: 5,
							top: 5,
						}}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				) : null}
			</DialogTitle>
			<DialogContent>
				<Stack mb={1}>
					<Stack direction="row" justifyContent="center" spacing={4}>
						<ButtonIconText
							disabled={props.disabledFirst}
							value={values.first}
							className={clsx({
								"btn-dialog-selection": true,
								"btn-selected": values.first === selected,
							})}
							icon={props.icons?.first}
							iconHover={props.icons?.firstHover}
							onClick={handleSelection}
							{...props.iconsProps?.first}
						/>
						<ButtonIconText
							disabled={props.disabledSecond}
							value={values.second}
							className={clsx({
								"btn-dialog-selection": true,
								"btn-selected": values.second === selected,
							})}
							icon={props.icons?.second}
							iconHover={props.icons?.secondHover}
							onClick={handleSelection}
							{...props.iconsProps?.second}
						/>
					</Stack>

					{!!selected && (
						<Stack mt={3}>
							<Divider className="!border-disabled" />
							<SelectionContent
								firstContent={
									props.firstContent ?? (
										<Typography pt={1} width="100%" className={"regular-pt-sans-pro-narrow-label"}>
											Adjustment resolved by check.
										</Typography>
									)
								}
								secondContent={
									props.secondContent ?? (
										<Typography pt={1} width="100%" className={"regular-pt-sans-pro-narrow-label"}>
											Adjustment rolled over to next commission cycle.
										</Typography>
									)
								}
								selected={getValue(selected)}
							/>
							<Stack justifyContent={"center"} alignItems={"center"} mt={2}>
								<Stack sx={{ maxWidth: "150px" }}>
									<Button
										variant="contained"
										minWidth={"120px"}
										text={
											props.loading ? (
												<CircularProgress style={{ width: "20px", height: "20px", color: "white" }} />
											) : (
												<>{props.buttonText ?? "Add New Adjustment"}</>
											)
										}
										classes="tier-btn tier-btn-free-width add-tier-btn"
										disabled={props.disabled || props.loading}
										onClick={handleAdd}
										bgColor={"var(--primary-color)"}
									></Button>
								</Stack>
							</Stack>
						</Stack>
					)}
				</Stack>
			</DialogContent>
		</Dialog>
	);
}
